// Design system dell'app, nato con il redesign "Scheda Cliente"
// (design_handoff_scheda_cliente/): font Plus Jakarta Sans / IBM Plex Mono,
// accento viola #5b5bd6, card bianche radius 18px senza ombre marcate.
// È la libreria di riferimento per OGNI nuovo sviluppo del frontend, non solo
// per la scheda cliente — vedi docs/11-design-system.md per la regola completa
// e il razionale. Le pagine più vecchie (shadcn default, font Public Sans/Outfit)
// vengono uniformate a questo stile una alla volta (es. Calendario); nel
// frattempo NON prendere quelle pagine come riferimento per codice nuovo.
//
// Le classi .scheda-font-ui / .scheda-font-mono (index.css) restano con questo
// nome per storia del progetto, ma sono le classi tipografiche ufficiali
// dell'intera app: vanno usate anche fuori dalla scheda cliente.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight, Info } from "lucide-react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { formatNumber } from "../utils/formatters";

// --- Token --------------------------------------------------------------
// Valori grezzi per quando serve un colore in `style={}` invece che in una
// className (grafici, SVG, inline style condizionali). Per le classi Tailwind
// arbitrarie (`text-[#...]`, `bg-[#...]`) copiare gli stessi esadecimali:
// non esiste un secondo sistema di token, questo è l'unico.
export const COLORS = {
  // Testo
  text: "#15161c",
  textSecondary: "#565d6d",
  textMuted: "#9095a1",
  textBody: "#3c414d",
  // Superfici e bordi neutri (dal più chiaro al più scuro)
  surface: "#fbfbfd",
  surfaceAlt: "#f3f3f8",
  border: "#e9e9f1",
  borderStrong: "#dcdce8",
  // Accento viola (brand)
  accent: "#5b5bd6",
  accentHover: "#4a4ac4",
  accentSoft: "#eeeefc",
  accentBorder: "#c9c9f0",
  // Semantica: successo / attivo
  success: "#0f7040",
  successStrong: "#12a05c",
  successSoft: "#e7f7ee",
  // Semantica: attenzione
  warning: "#b45309",
  warningStrong: "#f59e0b",
  warningSoft: "#fef3c7",
  // Semantica: errore / distruttivo
  danger: "#c0392b",
  dangerHover: "#a93226",
  dangerSoft: "#fdecea",
};

// Combinazioni bg/icona per il quadrato icona di SectionCard, gli avatar a
// iniziali e le pill di ruolo. Pescare da qui invece di inventare nuovi
// accoppiamenti hex tiene la palette dell'app chiusa e coerente.
export const ICON_TILES = {
  viola: { bg: "#eeeefc", color: "#5b5bd6" },   // dato primario / finanziario
  teal: { bg: "#e4f6f3", color: "#0d8377" },    // pacchetti / servizi
  neutro: { bg: "#f1f1f8", color: "#565d6d" },  // team / configurazione
  verde: { bg: "#e7f7ee", color: "#0f7040" },   // fatturato / esito positivo
  arancio: { bg: "#fff1e7", color: "#c2570f" }, // note / richiami
  rosa: { bg: "#fdeaf3", color: "#b83280" },    // ruolo videomaker
  ardesia: { bg: "#ececf3", color: "#3c414d" }, // ruoli senza colore proprio (amm, direzioni)
};

// Colore di un ruolo utente (avatar a iniziali, pill accanto al nome).
export function toneRuolo(ruolo) {
  switch (ruolo) {
    case "pm": return ICON_TILES.viola;
    case "smm": return ICON_TILES.teal;
    case "commerciale": return ICON_TILES.arancio;
    case "videomaker": return ICON_TILES.rosa;
    default: return ICON_TILES.ardesia;
  }
}

// Livelli di elevazione (handoff "Profondità", docs/11-design-system.md).
// Sempre due strati, contatto stretto + diffusa con spread negativo, su base
// rgba(15,23,42): mai nero puro. Niente ombre su divisori e righe di lista,
// che usano hover:bg-[#fbfbfd]. In className: shadow-[...] con gli stessi
// valori e gli spazi sostituiti da `_`.
export const SHADOWS = {
  inset: "inset 0 1px 2px rgba(15,23,42,.04)",                                     // 0 · campi a riposo
  appoggiato: "0 1px 2px rgba(15,23,42,.06), 0 4px 10px -4px rgba(15,23,42,.12)",   // 1 · tab attivo, chip, avatar
  card: "0 1px 2px rgba(15,23,42,.04), 0 16px 40px -18px rgba(15,23,42,.18)",       // 2 · card di contenuto
  flottante: "0 2px 6px rgba(15,23,42,.10), 0 24px 48px -16px rgba(15,23,42,.45)",  // 3 · barre flottanti, popover
  accento: "0 8px 20px -8px rgba(91,91,214,.9)",                                    // bottone primario in evidenza
  focus: "0 0 0 3px rgba(91,91,214,.14), 0 8px 18px -10px rgba(91,91,214,.5)",      // campo in focus
};

// Campo numerico con unità dentro il bordo (€ a sinistra, % a destra), per
// form di configurazione. Stati: riposo con ombra inset, hover bordo accento
// tenue, focus con alone e sollevamento di 1px, `modificato` (valore diverso
// dall'ultimo salvato) con fondo lilla chiaro.
// È un campo di testo, non type="number": quello non può mostrare il punto
// delle migliaia (regola dell'app, CLAUDE.md). A riposo il valore è formattato
// it-IT (1.500, 12,5); mentre si scrive resta il testo digitato, e a `onChange`
// arriva la stringa numerica normalizzata ("1500", "12.5").
export const NUMBER_FIELD_CLS =
  "w-full h-[38px] rounded-[10px] border text-right text-[13.5px] text-[#15161c] scheda-font-mono outline-none " +
  "shadow-[inset_0_1px_2px_rgba(15,23,42,.04)] hover:border-[#c9c9f0] " +
  "focus:border-[#5b5bd6] focus:shadow-[0_0_0_3px_rgba(91,91,214,.14),0_8px_18px_-10px_rgba(91,91,214,.5)] focus:-translate-y-px " +
  "transition-[box-shadow,border-color,transform] duration-200";

// "1.500,5" → "1500.5": in italiano il punto raggruppa e la virgola separa i decimali.
function daTestoItaliano(testo) {
  return testo.replace(/\s/g, "").replace(/\./g, "").replace(",", ".").replace(/[^0-9.-]/g, "");
}

export function NumberField({ value, onChange, unit = "€", modificato = false, testId, ...rest }) {
  const prefisso = unit === "€";
  const [bozza, setBozza] = useState(null);
  const formattato = value === null || value === undefined || value === "" ? "" : formatNumber(value);
  return (
    <span className="relative block">
      {prefisso && <span className="absolute left-[11px] top-[10px] text-[13px] text-[#b4b8c5] pointer-events-none">€</span>}
      <input
        type="text"
        inputMode="decimal"
        value={bozza ?? formattato}
        onFocus={() => setBozza(formattato)}
        onBlur={() => setBozza(null)}
        onChange={(e) => {
          setBozza(e.target.value);
          onChange(daTestoItaliano(e.target.value));
        }}
        className={`${NUMBER_FIELD_CLS} ${prefisso ? "pl-6 pr-[11px]" : "pl-2.5"} ${modificato ? "border-[#c9c9f0] bg-[#f7f7fe]" : "border-[#e4e4ed] bg-white"}`}
        // Il suffisso può essere più lungo di un carattere ("€/mese"): lo spazio
        // a destra segue la sua lunghezza, così non copre la cifra.
        style={prefisso ? undefined : { paddingRight: 18 + unit.length * 7.5 }}
        data-testid={testId}
        {...rest}
      />
      {!prefisso && <span className="absolute right-[11px] top-[10px] text-[13px] text-[#9095a1] pointer-events-none">{unit}</span>}
    </span>
  );
}

// Parallasse al movimento del puntatore: i livelli registrati con `layer(k)` si
// spostano di k px (coordinate normalizzate −1…1), con k di segno opposto per
// livelli in controfase. Scrive `transform` direttamente sugli elementi invece
// di passare dallo stato React: un setState a ogni frame ridisegnerebbe l'intera
// pagina. Spento con prefers-reduced-motion e senza puntatore fine (touch).
// Attenzione: un livello con transform diventa il riferimento dei discendenti
// `position: fixed`, che vanno quindi resi in un portal (vedi FloatingSaveBar).
//
// Con un menu, un popover o un dialog aperto il parallasse si ferma: il
// componente flottante è agganciato a un campo dentro un livello, e se il campo
// scivola Radix ne ricalcola posizione e altezza a ogni frame. Il risultato era
// un ciclo di ResizeObserver (centinaia di errori aprendo la select clienti di
// Config per cliente) e un menu che trema.
const PARALLAX_TRANSITION = "transform .6s cubic-bezier(.22,.7,.3,1)";
const FLOTTANTE_APERTO = "[data-radix-popper-content-wrapper], [role='dialog'], [role='alertdialog'], [role='listbox'], [role='menu']";

export function useParallax() {
  const livelli = useRef(new Map());
  const refCache = useRef({});
  const raf = useRef(0);
  const attivo = useRef(false);

  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-reduced-motion: no-preference) and (pointer: fine)");
    attivo.current = !!mq?.matches;
    const aggiorna = () => { attivo.current = !!mq?.matches; };
    mq?.addEventListener?.("change", aggiorna);
    return () => {
      mq?.removeEventListener?.("change", aggiorna);
      cancelAnimationFrame(raf.current);
    };
  }, []);

  const fermo = useRef(false);

  const applica = (x, y) => {
    livelli.current.forEach((k, el) => {
      if (fermo.current) el.style.transition = PARALLAX_TRANSITION;
      el.style.transform = `translate3d(${(x * k).toFixed(1)}px,${(y * k).toFixed(1)}px,0)`;
    });
    fermo.current = false;
  };

  // Al clic i livelli si bloccano dove sono, anche a metà transizione: il clic
  // può aprire un menu agganciato a un campo che altrimenti scivolerebbe ancora
  // per 0,6s. Riprendono al primo movimento dopo la chiusura del menu.
  const onPointerDownCapture = useCallback(() => {
    cancelAnimationFrame(raf.current);
    livelli.current.forEach((_, el) => {
      const attuale = getComputedStyle(el).transform;
      el.style.transition = "none";
      el.style.transform = attuale === "none" ? "" : attuale;
    });
    fermo.current = true;
  }, []);

  const onMouseMove = useCallback((e) => {
    if (!attivo.current) return;
    if (document.querySelector(FLOTTANTE_APERTO)) return;
    const r = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * 2 - 1;
    const y = ((e.clientY - r.top) / r.height) * 2 - 1;
    cancelAnimationFrame(raf.current);
    raf.current = requestAnimationFrame(() => applica(x, y));
  }, []);

  const onMouseLeave = useCallback(() => {
    cancelAnimationFrame(raf.current);
    if (document.querySelector(FLOTTANTE_APERTO)) return;
    applica(0, 0);
  }, []);

  // Ref callback stabile per fattore: un'identità nuova a ogni render farebbe
  // staccare e riattaccare il livello a ogni aggiornamento della pagina.
  const layer = useCallback((k) => {
    if (!refCache.current[k]) {
      let corrente = null;
      refCache.current[k] = (el) => {
        if (corrente) livelli.current.delete(corrente);
        corrente = el;
        if (el) {
          el.style.transition = PARALLAX_TRANSITION;
          livelli.current.set(el, k);
        }
      };
    }
    return refCache.current[k];
  }, []);

  return { bind: { onMouseMove, onMouseLeave, onPointerDownCapture }, layer };
}

// Barra di salvataggio flottante in basso al centro, per form lunghi con molti
// campi indipendenti: sostituisce il bottone "Salva" in coda al form. Visibile
// solo con modifiche pendenti o per 2,5s dopo un salvataggio riuscito
// (`salvato`). Resa in un portal su body: dentro un livello di parallasse
// `position: fixed` si aggancerebbe all'elemento trasformato, non alla finestra.
// Su desktop è centrata sull'area contenuti, a destra della sidebar di 256px;
// su mobile sta sopra la barra di navigazione in basso.
export function FloatingSaveBar({ modifiche, salvato, saving, onAnnulla, onSalva, labelSalva = "Salva configurazione", testId }) {
  const visibile = modifiche > 0 || salvato;
  const testo = modifiche > 1
    ? `${modifiche} modifiche non salvate`
    : modifiche === 1 ? "1 modifica non salvata" : "Configurazione salvata";
  return createPortal(
    <div
      className="fixed left-1/2 lg:left-[calc(50%+8rem)] bottom-24 lg:bottom-7 z-40 flex items-center gap-3.5 pl-[18px] pr-2 py-2 rounded-[16px] bg-[rgba(21,22,28,.92)] backdrop-blur-[12px] whitespace-nowrap scheda-font-ui motion-reduce:transition-none"
      style={{
        boxShadow: SHADOWS.flottante,
        opacity: visibile ? 1 : 0,
        transform: `translate(-50%, ${visibile ? 0 : 24}px)`,
        pointerEvents: visibile ? "auto" : "none",
        transition: "opacity .3s, transform .4s cubic-bezier(.22,.7,.3,1)",
      }}
      aria-hidden={!visibile}
      data-testid={testId}
    >
      <span
        className="w-2 h-2 rounded-full shrink-0"
        style={{ background: modifiche > 0 ? COLORS.warningStrong : "#10b981", boxShadow: "0 0 0 4px rgba(255,255,255,.08)" }}
      />
      <span className="text-[13px] font-medium text-white">{testo}</span>
      {modifiche > 0 && (
        <>
          <button
            type="button"
            onClick={onAnnulla}
            disabled={saving}
            className="h-9 px-3.5 rounded-[10px] bg-white/[.08] hover:bg-white/[.14] text-[#d7d9e2] text-[13px] font-semibold transition-colors disabled:opacity-50"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={onSalva}
            disabled={saving}
            className="h-9 px-4 rounded-[10px] bg-[#5b5bd6] hover:bg-[#6b6be0] text-white text-[13px] font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ boxShadow: SHADOWS.accento }}
            data-testid={testId ? `${testId}-salva` : undefined}
          >
            {saving ? "Salvataggio..." : labelSalva}
          </button>
        </>
      )}
    </div>,
    document.body
  );
}

// Raggi standard (Tailwind arbitrario, es. rounded-[18px]) — non ce ne sono
// altri in uso, non introdurne di nuovi senza motivo:
//   8px  chip, icone piccole, pulsanti quadrati piccoli
//   10px pulsanti, campi form, badge rettangolari (il default per "controllo interattivo")
//   12px tile icona, box informativi dentro un dialog
//   14px popover, dialog di conferma piccoli, box vuoti tratteggiati
//   18px card di sezione (SectionCard), dialog di conferma standard
//   22px dialog ricchi/con form (es. AlertRichiamoDialog)
//   full pill (badge di stato, avatar)

export const SECONDARY_BTN =
  "h-8 px-3 rounded-[10px] border border-[#e0e0ea] bg-white text-[12.5px] font-semibold text-[#565d6d] hover:border-[#c9c9f0] hover:text-[#5b5bd6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed scheda-font-ui inline-flex items-center gap-1 shrink-0";
export const PRIMARY_BTN =
  "h-9 px-4 rounded-[10px] bg-[#5b5bd6] hover:bg-[#4a4ac4] text-white text-[13px] font-semibold transition-colors disabled:bg-[#c7c7e4] disabled:cursor-not-allowed scheda-font-ui inline-flex items-center justify-center gap-1.5";
// Variante compatta di PRIMARY_BTN, alta quanto SECONDARY_BTN: per l'azione
// principale di una riga di lista accanto a bottoni secondari (es. "Invia per
// la firma" nei Contratti), dove l'altezza piena romperebbe l'allineamento.
export const PRIMARY_BTN_SM =
  "h-8 px-3 rounded-[10px] bg-[#5b5bd6] hover:bg-[#4a4ac4] text-white text-[12.5px] font-semibold transition-colors disabled:bg-[#c7c7e4] disabled:cursor-not-allowed scheda-font-ui inline-flex items-center justify-center gap-1.5 shrink-0";
export const GHOST_BTN =
  "h-8 px-2.5 rounded-[10px] text-[12.5px] font-semibold text-[#9095a1] hover:text-[#565d6d] transition-colors scheda-font-ui inline-flex items-center gap-1 disabled:opacity-50";
// GHOST_BTN in colore accento: azione di creazione in coda a una lista
// (es. "+ Contratto aggiuntivo"), visibile ma senza il peso di un bottone pieno.
export const GHOST_ACCENT_BTN =
  "h-8 px-2.5 rounded-[10px] text-[12.5px] font-semibold text-[#5b5bd6] hover:bg-[#eeeefc] transition-colors scheda-font-ui inline-flex items-center gap-1 disabled:opacity-50";
export const FIELD_CLS =
  "h-9 rounded-[10px] border border-[#e4e4ed] bg-white text-[13.5px] px-3 shadow-none focus-visible:ring-0 focus:border-[#5b5bd6] focus:shadow-[0_0_0_3px_rgba(91,91,214,.14)] transition-shadow scheda-font-ui";
// Variante outline per azioni distruttive (es. "Elimina") — mai il rosso pieno
// come CTA primaria, solo come conferma dentro un dialog o come azione
// secondaria esplicita: il pieno #c0392b è riservato al pulsante di conferma
// dentro un AlertDialog ("Elimina definitivamente"), vedi DIALOG_CONTENT_CLS.
export const DANGER_BTN =
  "h-8 px-3 rounded-[10px] border border-[#f0d6d2] bg-white text-[12.5px] font-semibold text-[#c0392b] hover:bg-[#fdecea] hover:border-[#f3c9c3] transition-colors disabled:opacity-50 disabled:cursor-not-allowed scheda-font-ui inline-flex items-center gap-1.5 shrink-0";

// Contenuto di un AlertDialog/Dialog di conferma standard (radice 18px). Per
// dialog più ricchi con form al loro interno (es. AlertRichiamoDialog) si usa
// invece rounded-[22px]: è l'unica altra misura legittima, non introdurne altre.
export const DIALOG_CONTENT_CLS =
  "scheda-font-ui max-w-md rounded-[18px] border-[#e9e9f1] duration-300 motion-reduce:animate-none";

// `collapsible` la rende una sezione a fisarmonica: l'intestazione apre e chiude
// il corpo, lo stato lo tiene il chiamante (`open`, `onToggle`). Il corpo si
// anima su grid-template-rows (0fr → 1fr), che non richiede di misurarne
// l'altezza, con la curva comune dell'app. `action` resta fuori dal pulsante di
// apertura: un bottone non può stare dentro un altro.
export function SectionCard({ icon: Icon, iconBg, iconColor, title, count, action, children, dense = false, testId, innerRef, collapsible = false, open = true, onToggle }) {
  const padX = dense ? 22 : 26;
  const aperta = !collapsible || open;
  const intestazione = (
    <>
      <div className="w-[33px] h-[33px] rounded-[10px] flex items-center justify-center shrink-0" style={{ background: iconBg }}>
        <Icon className="w-4 h-4" style={{ color: iconColor }} strokeWidth={1.85} />
      </div>
      <h2 className="text-[16.5px] font-bold text-[#15161c]">{title}</h2>
      {count != null && (
        <span className="h-[22px] px-[9px] rounded-full bg-[#f3f3f8] text-[#6b7280] text-[11.5px] font-semibold scheda-font-mono flex items-center">{count}</span>
      )}
    </>
  );
  const chevron = collapsible && (
    <button
      type="button"
      onClick={onToggle}
      tabIndex={-1}
      aria-hidden="true"
      className="w-8 h-8 shrink-0 rounded-[10px] flex items-center justify-center hover:bg-[#f3f3f8] transition-colors"
    >
      <ChevronDown
        className={`w-[18px] h-[18px] text-[#565d6d] transition-transform duration-300 ease-[cubic-bezier(.22,.7,.3,1)] motion-reduce:transition-none ${aperta ? "rotate-180" : ""}`}
        strokeWidth={1.8}
      />
    </button>
  );
  return (
    // Su mobile il padding orizzontale scende a 16px: con 26px più il gutter
    // della pagina, su 390px al contenuto restavano meno di 300px.
    <div ref={innerRef} className="bg-white border border-[#e9e9f1] rounded-[18px] shadow-[0_2px_12px_rgba(0,0,0,0.03)] scheda-font-ui" style={{ "--sc-pad": `${padX}px` }} data-testid={testId}>
      <div
        className="flex items-center gap-2.5 flex-wrap px-4 sm:px-[var(--sc-pad)]"
        style={{ paddingTop: 22, paddingBottom: collapsible && !aperta ? 22 : 16, transition: "padding .3s cubic-bezier(.22,.7,.3,1)" }}
      >
        {collapsible ? (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={aperta}
            className="flex-1 min-w-0 flex items-center gap-2.5 text-left rounded-[10px] focus-visible:outline-none focus-visible:shadow-[0_0_0_3px_rgba(91,91,214,.14)]"
            data-testid={testId ? `${testId}-toggle` : undefined}
          >
            {intestazione}
          </button>
        ) : (
          <>
            {intestazione}
            <div className="flex-1 min-w-[8px]" />
          </>
        )}
        {action}
        {chevron}
      </div>
      {collapsible ? (
        <div
          className="grid transition-[grid-template-rows] duration-300 ease-[cubic-bezier(.22,.7,.3,1)] motion-reduce:transition-none"
          style={{ gridTemplateRows: aperta ? "1fr" : "0fr" }}
          inert={!aperta}
        >
          <div className="overflow-hidden min-h-0">
            <div className="px-4 pb-4 sm:px-[var(--sc-pad)] sm:pb-[var(--sc-pad)]">{children}</div>
          </div>
        </div>
      ) : (
        <div className="px-4 pb-4 sm:px-[var(--sc-pad)] sm:pb-[var(--sc-pad)]">{children}</div>
      )}
    </div>
  );
}

export function StatBlock({ label, value, color }) {
  return (
    <div className="text-right shrink-0">
      <div className="text-[10.5px] font-semibold uppercase tracking-wide text-[#9095a1]">{label}</div>
      <div className="text-[18px] font-bold scheda-font-mono" style={{ color }}>{value}</div>
    </div>
  );
}

export function StatusPill({ active, activeLabel = "Attivo", inactiveLabel = "Inattivo" }) {
  return active ? (
    <span className="inline-flex items-center gap-1.5 h-[26px] px-[11px] rounded-full bg-[#e7f7ee] text-[#0f7040] text-[12.5px] font-semibold scheda-font-ui shrink-0">
      <span className="w-1.5 h-1.5 rounded-full bg-[#12a05c]" />
      {activeLabel}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 h-[26px] px-[11px] rounded-full bg-[#f3f3f8] text-[#6b7280] text-[12.5px] font-semibold scheda-font-ui shrink-0">
      <span className="w-1.5 h-1.5 rounded-full bg-[#9095a1]" />
      {inactiveLabel}
    </span>
  );
}

// Badge di stato generico con tre livelli di gravità (più "neutro"). Estrae
// il pattern colore→testo/sfondo/bordo/pallino già usato ad hoc per la
// priorità dell'Alert/Richiamo (AlertRichiamoDialog) e per i pallini del
// semaforo cliente: nuovi badge di questo tipo devono passare da qui invece
// di ridefinire la stessa combinazione di hex in un altro file.
const BADGE_TONES = {
  success: { color: "text-[#0f7040]", bg: "bg-[#e7f7ee]", dot: "bg-[#10b981]" },
  warning: { color: "text-[#b45309]", bg: "bg-[#fef3c7]", dot: "bg-[#f59e0b]" },
  danger: { color: "text-[#c0392b]", bg: "bg-[#fdecea]", dot: "bg-[#e02424]" },
  neutral: { color: "text-[#6b7280]", bg: "bg-[#f3f3f8]", dot: "bg-[#9095a1]" },
  // Non una gravità ma "tocca a noi": stato che chiede un'azione dell'utente
  // (es. contratto "Da inviare"), distinto dall'attesa di terzi (warning).
  accent: { color: "text-[#5b5bd6]", bg: "bg-[#eeeefc]", dot: "bg-[#5b5bd6]" },
};

export function Badge({ tone = "neutral", dot = false, testId, children }) {
  const t = BADGE_TONES[tone] || BADGE_TONES.neutral;
  return (
    <span className={`inline-flex items-center gap-1.5 h-[22px] px-[9px] rounded-full text-[11.5px] font-semibold scheda-font-ui shrink-0 ${t.color} ${t.bg}`} data-testid={testId}>
      {dot && <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} />}
      {children}
    </span>
  );
}

// Targhetta "New" per una funzionalità appena rilasciata. Sparisce da sola dopo
// `fino` (data "YYYY-MM-DD", inclusa): niente da ricordarsi di togliere, ma
// il codice morto resta finché non si ripulisce. `floating` la appoggia
// sull'angolo in alto a destra del contenitore, che deve essere `relative`.
export function BadgeNew({ fino, floating = false, testId }) {
  if (!fino || new Date() > new Date(`${fino}T23:59:59`)) return null;
  return (
    <span
      className={`inline-flex items-center h-[17px] px-[6px] rounded-full bg-[#5b5bd6] text-white text-[9.5px] font-bold uppercase tracking-[.06em] scheda-font-ui shrink-0 pointer-events-none ${floating ? "absolute -top-2 -right-2 z-10 shadow-[0_2px_6px_rgba(91,91,214,.35)]" : ""}`}
      data-testid={testId}
    >
      New
    </span>
  );
}

// Placeholder per liste/sezioni senza dati ("Nessun cliente trovato", "Nessun
// servizio configurato", ...). Il pattern testuale semplice (senza bordo
// tratteggiato) per una riga isolata dentro una SectionCard; passare `boxed`
// per il contenitore tratteggiato usato nei form/dialog quando lo stato
// vuoto è il corpo principale del blocco, non una riga tra altre.
export function EmptyState({ children, boxed = false }) {
  if (boxed) {
    return (
      <div className="p-4 rounded-[14px] border border-dashed border-[#e4e4ed] bg-[#fafafc] text-center">
        <p className="text-[13px] text-[#9095a1] italic scheda-font-ui">{children}</p>
      </div>
    );
  }
  return <p className="text-[13.5px] font-medium text-[#9095a1] italic scheda-font-ui">{children}</p>;
}

// Tooltip descrittivo al passaggio del mouse (o al focus da tastiera): titolo
// e riga di spiegazione su fondo accento viola, raggio 14px da popover.
// Colore pieno e ombra più presente (è un elemento flottante, non una
// superficie di pagina): deve staccarsi dalle card bianche su cui compare.
// Sostituisce l'attributo `title` nativo, che non si può stilizzare. Il
// trigger è uno span inline-flex attorno ai figli: così funziona anche con
// componenti che non inoltrano il ref (Chip, StatBlock), e il focus di un
// pulsante contenuto apre comunque il tooltip. `block` rende il trigger largo
// quanto il contenitore, per elementi a tutta larghezza (barre, card).
export function InfoTooltip({ title, description, side = "top", block = false, children }) {
  return (
    <TooltipPrimitive.Provider delayDuration={250} skipDelayDuration={150}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>
          <span className={block ? "flex w-full" : "inline-flex"}>{children}</span>
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side={side}
            sideOffset={8}
            collisionPadding={12}
            className="z-50 max-w-[260px] px-3 py-2.5 rounded-[14px] border border-[#4a4ac4] bg-[#5b5bd6] shadow-[0_14px_36px_rgba(91,91,214,0.38),0_3px_8px_rgba(15,23,42,0.12)] scheda-font-ui scheda-pop-anim"
          >
            {title && <div className="text-[12.5px] font-semibold text-white">{title}</div>}
            {description && <div className={`text-[12px] leading-[1.45] text-white/85 ${title ? "mt-0.5" : ""}`}>{description}</div>}
            <TooltipPrimitive.Arrow className="fill-[#5b5bd6]" width={12} height={6} />
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}

// onClick serve per i link interni all'app (navigate di react-router): un href
// su una rotta interna ricaricherebbe l'intera pagina. external apre href in
// una nuova scheda (link a Drive e simili). accent evidenzia il chip coi toni
// soft del brand; badge aggiunge una pecetta in alto a destra (es. "News" per
// segnalare una funzione appena introdotta). size="sm" è la versione compatta
// (24px, testo 12px) per i chip dentro le righe di una lista.
// Icona "i" che apre un InfoTooltip: spiega un'etichetta, un KPI o un'intestazione
// di colonna senza allungare il testo in pagina. È un pulsante vero, così il
// tooltip si apre anche dalla tastiera.
export function InfoHint({ title, description, side = "top", testId }) {
  return (
    <InfoTooltip title={title} description={description} side={side}>
      <button
        type="button"
        aria-label={title || "Informazioni"}
        className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[#b4b8c5] hover:text-[#5b5bd6] focus-visible:text-[#5b5bd6] focus-visible:outline-none focus-visible:shadow-[0_0_0_3px_rgba(91,91,214,.14)] transition-colors align-middle normal-case tracking-normal"
        data-testid={testId}
      >
        <Info className="w-3.5 h-3.5" strokeWidth={2} />
      </button>
    </InfoTooltip>
  );
}

// Iniziali di un nome per l'avatar: "Roberto Diecixcento" → "RD". Dai nomi
// account si tolgono prima le parti tra parentesi e il suffisso numerico
// ("commerciale10x100" → "C"), che altrimenti darebbe iniziali senza senso.
export function iniziali(nome) {
  const pulito = (nome || "").replace(/\(.*?\)/g, "").replace(/\d+(?:x\d+)*$/i, "").trim();
  if (!pulito) return "—";
  return pulito.split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
}

// Avatar di un utente: la foto profilo (`picture`, di solito da Google) se c'è,
// altrimenti le iniziali su gradiente accento. Se la foto non si carica (link
// scaduto, bloccato) torna alle iniziali invece di mostrare un'immagine rotta.
// referrerPolicy no-referrer: le foto di googleusercontent rispondono 403 a
// richieste con referrer esterno.
// `shape="rounded"` dà la forma delle tile icona (13px a 42px), per le card
// persona. `tone` ({ bg, color }, es. toneRuolo(role)) colora le iniziali;
// senza, restano sul gradiente accento. Con la foto il colore non si vede.
export function UserAvatar({ name, picture, size = 26, shape = "circle", tone, className = "" }) {
  const [rotta, setRotta] = useState(false);
  useEffect(() => setRotta(false), [picture]);
  const mostraFoto = picture && !rotta;
  const raggio = shape === "rounded" ? Math.round(size * 0.31) : 9999;
  return (
    <span
      className={`inline-flex items-center justify-center overflow-hidden shrink-0 font-bold scheda-font-ui ${tone ? "" : "text-[#5b5bd6] bg-gradient-to-br from-[#eeeefc] to-[#dcdcf8]"} ${className}`}
      style={{
        width: size,
        height: size,
        borderRadius: raggio,
        fontSize: Math.max(9, Math.round(size * (shape === "rounded" ? 0.34 : 0.4))),
        boxShadow: SHADOWS.appoggiato,
        ...(tone && !mostraFoto ? { background: tone.bg, color: tone.color } : {}),
      }}
      aria-hidden="true"
    >
      {mostraFoto ? (
        <img src={picture} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" onError={() => setRotta(true)} />
      ) : (
        iniziali(name)
      )}
    </span>
  );
}

// Interruttore on/off (40×24) per attivare una sezione intera, al posto di una
// Checkbox quando lo stato cambia cosa compare sotto. role="switch" per gli
// screen reader; `testId` resta sul pulsante, come prima sulla Checkbox.
export function ToggleSwitch({ checked, onChange, disabled = false, label, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="relative w-10 h-6 rounded-full shrink-0 transition-colors duration-[220ms] ease-[cubic-bezier(.22,.7,.3,1)] shadow-[inset_0_1px_2px_rgba(15,23,42,.12)] focus-visible:outline-none focus-visible:shadow-[0_0_0_3px_rgba(91,91,214,.2)] disabled:opacity-50 disabled:cursor-not-allowed"
      style={{ background: checked ? COLORS.accent : "#dcdce8" }}
      data-testid={testId}
      data-state={checked ? "checked" : "unchecked"}
    >
      <span
        className="absolute top-[3px] left-[3px] w-[18px] h-[18px] rounded-full bg-white transition-transform duration-[220ms] ease-[cubic-bezier(.22,.7,.3,1)] motion-reduce:transition-none"
        style={{ transform: `translateX(${checked ? 16 : 0}px)`, boxShadow: "0 1px 3px rgba(15,23,42,.25)" }}
      />
    </button>
  );
}

// Chip selezionabile con casella (32px), per scegliere più opzioni affiancate
// (es. servizi erogati) al posto di una fila di Checkbox con etichetta.
export function ToggleChip({ checked, onChange, children, disabled = false, testId }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={!!checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`h-8 px-2.5 rounded-[10px] border inline-flex items-center gap-2 text-[12.5px] font-semibold transition-colors scheda-font-ui disabled:opacity-50 ${
        checked ? "bg-[#eeeefc] border-[#c9c9f0] text-[#5b5bd6]" : "bg-white border-[#e4e4ed] text-[#565d6d] hover:border-[#c9c9f0]"
      }`}
      data-testid={testId}
      data-state={checked ? "checked" : "unchecked"}
    >
      <span
        className={`w-3.5 h-3.5 rounded-[4px] border flex items-center justify-center shrink-0 ${checked ? "bg-[#5b5bd6] border-[#5b5bd6]" : "bg-white border-[#c9ccd6]"}`}
        aria-hidden="true"
      >
        {checked && (
          <svg viewBox="0 0 12 12" className="w-2.5 h-2.5" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2.5 6.2 5 8.5l4.5-5" />
          </svg>
        )}
      </span>
      {children}
    </button>
  );
}

// Selettore a segmenti (una scelta fra poche opzioni brevi, es. "1× / 2× al mese").
// `options`: [{ value, label, testId }].
export function SegmentedControl({ value, onChange, options, disabled = false }) {
  return (
    <div className="inline-flex p-[3px] rounded-[10px] bg-[#f3f3f8] gap-0.5 scheda-font-ui" role="radiogroup">
      {options.map((o) => {
        const attivo = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={attivo}
            disabled={disabled}
            onClick={() => onChange(o.value)}
            className={`h-[26px] px-2.5 rounded-[8px] text-[12.5px] font-semibold transition-colors whitespace-nowrap disabled:opacity-50 ${attivo ? "bg-white text-[#15161c]" : "text-[#6b7280] hover:text-[#15161c]"}`}
            style={attivo ? { boxShadow: SHADOWS.appoggiato } : undefined}
            data-testid={o.testId}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function Chip({ icon: Icon, children, mono, href, external, onClick, testId, accent, badge, size }) {
  const tono = accent
    ? "bg-[#eeeefc] border-[#c9c9f0] text-[#5b5bd6] font-semibold"
    : "bg-white border-[#e7e7ef] text-[#3c414d] font-medium";
  const misura = size === "sm" ? "h-6 px-2 text-[12px]" : "h-7 px-2.5 text-[13px]";
  const cls = `relative ${misura} rounded-[8px] border ${tono} inline-flex items-center gap-1.5 scheda-font-ui`;
  const clsLink = accent
    ? `${cls} hover:border-[#5b5bd6] transition-colors`
    : `${cls} hover:border-[#c9c9f0] hover:text-[#5b5bd6] transition-colors`;
  const inner = (
    <>
      {Icon && <Icon className={`w-3.5 h-3.5 shrink-0 ${accent ? "text-[#5b5bd6]" : "text-[#9095a1]"}`} strokeWidth={1.8} />}
      <span className={mono ? "scheda-font-mono" : ""}>{children}</span>
      {badge && (
        <span className="absolute -top-2 -right-2 h-4 px-1.5 rounded-full bg-[#5b5bd6] text-white text-[9.5px] font-bold uppercase tracking-[.06em] leading-none inline-flex items-center shadow-sm">
          {badge}
        </span>
      )}
    </>
  );
  if (onClick) {
    return <button type="button" onClick={onClick} className={clsLink} data-testid={testId}>{inner}</button>;
  }
  if (href) {
    return (
      <a
        href={href}
        className={clsLink}
        data-testid={testId}
        {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      >
        {inner}
      </a>
    );
  }
  return <span className={cls}>{inner}</span>;
}


// Pannello a comparsa ancorato a un campo (usato da MonthPicker e DatePicker).
// Fuori dai dialog va in un portal su <body> con position:fixed: così nessun
// contenitore con overflow hidden/auto (card, liste) lo taglia, e se sotto al
// campo non c'è spazio si apre verso l'alto. Dentro un dialog Radix resta
// inline sotto al campo: un portal esterno verrebbe considerato "fuori" dal
// dialog (click che lo chiudono, focus trap) e il transform del dialog
// sposterebbe comunque il riferimento del position:fixed.
function PopoverAncorato({ anchorRef, open, onClose, width, children }) {
  const popRef = useRef(null);
  // Dentro un dialog Radix il popover non può andare su document.body: il
  // dialog rende inerte tutto ciò che sta fuori. Va quindi nella radice del
  // dialog, non accanto all'ancora: il corpo di un dialog di solito scorre
  // (overflow-y-auto) e taglierebbe il popover sotto il footer.
  const [dialog, setDialog] = useState(null);
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    setDialog(anchorRef.current?.closest('[role="dialog"]') || null);
  }, [open, anchorRef]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const calcola = () => {
      const a = anchorRef.current?.getBoundingClientRect();
      if (!a) return;
      // Fuori da un dialog i limiti sono la finestra; dentro, il dialog, che
      // è anche il riferimento delle coordinate (position: absolute).
      const box = dialog
        ? dialog.getBoundingClientRect()
        : { top: 0, left: 0, bottom: window.innerHeight, right: window.innerWidth };
      const h = popRef.current?.offsetHeight || 0;
      const gap = 6;
      const margine = 8;
      const sottoOk = a.bottom + gap + h <= box.bottom - margine;
      const sopraOk = a.top - gap - h >= box.top + margine;
      const top = !sottoOk && sopraOk ? a.top - gap - h : a.bottom + gap;
      const left = Math.max(box.left + margine, Math.min(a.left, box.right - width - margine));
      setPos(dialog
        ? { top: top - box.top + dialog.scrollTop, left: left - box.left + dialog.scrollLeft }
        : { top, left });
    };
    calcola();
    // Seconda misura dopo il primo paint: l'altezza reale serve per decidere
    // se aprire verso l'alto.
    const raf = requestAnimationFrame(calcola);
    window.addEventListener("scroll", calcola, true);
    window.addEventListener("resize", calcola);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", calcola, true);
      window.removeEventListener("resize", calcola);
    };
  }, [open, dialog, anchorRef, width]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (anchorRef.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      onClose();
    };
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;
  const cls = "bg-white border border-[#e9e9f1] rounded-[14px] shadow-lg p-2 scheda-pop-anim scheda-font-ui";
  return createPortal(
    <div
      ref={popRef}
      className={`${dialog ? "absolute" : "fixed"} z-[60] ${cls}`}
      // Prima della misura resta invisibile, così non lampeggia in alto a sinistra.
      style={{ width, top: pos?.top ?? 0, left: pos?.left ?? 0, visibility: pos ? "visible" : "hidden" }}
    >
      {children}
    </div>,
    dialog || document.body,
  );
}

const MESI_BREVI = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];

// Sostituisce <input type="month">: il selettore nativo del browser non si può
// restilizzare (è chrome del sistema operativo, non DOM), quindi qui è un vero
// popover disegnato con i token della pagina. value/onChange usano la stessa
// stringa "YYYY-MM" dell'input nativo che sostituisce — onChange riceve
// direttamente il nuovo valore (non un evento).
export function MonthPicker({ value, onChange, className = "", placeholder = "Seleziona mese", ...rest }) {
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(() => (value ? Number(value.slice(0, 4)) : new Date().getFullYear()));
  const ref = useRef(null);
  const chiudi = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (open) setViewYear(value ? Number(value.slice(0, 4)) : new Date().getFullYear());
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedYear = value ? Number(value.slice(0, 4)) : null;
  const selectedMonthIdx = value ? Number(value.slice(5, 7)) - 1 : null;
  const label = value ? `${MESI_BREVI[selectedMonthIdx]} ${selectedYear}` : placeholder;

  const pick = (monthIdx) => {
    onChange(`${viewYear}-${String(monthIdx + 1).padStart(2, "0")}`);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`${className} w-full flex items-center justify-between gap-2 text-left`}
        {...rest}
      >
        <span className={value ? "scheda-font-mono" : "text-[#9095a1]"}>{label}</span>
        <CalendarDays className="w-4 h-4 text-[#9095a1] shrink-0" strokeWidth={1.8} />
      </button>
      <PopoverAncorato anchorRef={ref} open={open} onClose={chiudi} width={240}>
          <div className="flex items-center justify-between px-1 py-1 mb-1">
            <button type="button" onClick={() => setViewYear(y => y - 1)} className="w-7 h-7 rounded-[8px] flex items-center justify-center text-[#565d6d] hover:bg-[#f3f3f8] transition-colors">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-[13.5px] font-bold text-[#15161c] scheda-font-mono">{viewYear}</span>
            <button type="button" onClick={() => setViewYear(y => y + 1)} className="w-7 h-7 rounded-[8px] flex items-center justify-center text-[#565d6d] hover:bg-[#f3f3f8] transition-colors">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-4 gap-1">
            {MESI_BREVI.map((m, i) => {
              const isSelected = selectedYear === viewYear && selectedMonthIdx === i;
              return (
                <button
                  type="button"
                  key={m}
                  onClick={() => pick(i)}
                  className={`h-8 rounded-[8px] text-[12.5px] font-medium transition-colors ${
                    isSelected ? "bg-[#5b5bd6] text-white" : "text-[#3c414d] hover:bg-[#f3f3f8]"
                  }`}
                >
                  {m}
                </button>
              );
            })}
          </div>
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#f0f0f6] px-1">
            <button type="button" onClick={() => { onChange(""); setOpen(false); }} className="text-[12px] font-semibold text-[#9095a1] hover:text-[#565d6d] transition-colors">
              Cancella
            </button>
            <button
              type="button"
              onClick={() => {
                const now = new Date();
                onChange(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
                setOpen(false);
              }}
              className="text-[12px] font-semibold text-[#5b5bd6] hover:text-[#4a4ac4] transition-colors"
            >
              Questo mese
            </button>
          </div>
      </PopoverAncorato>
    </div>
  );
}

const MESI_LUNGHI = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];
const GIORNI_SETTIMANA = ["L", "M", "M", "G", "V", "S", "D"];

const pad2 = (n) => String(n).padStart(2, "0");
// Data locale → "YYYY-MM-DD" senza passare da toISOString, che converte in UTC
// e intorno a mezzanotte sposterebbe il giorno.
const toIsoLocale = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

// Sostituisce <input type="date"> per lo stesso motivo di MonthPicker: il
// calendario nativo non si può restilizzare. value/onChange usano la stringa
// "YYYY-MM-DD" dell'input nativo; onChange riceve il valore, non un evento.
// Settimana da lunedì, come il calendario italiano.
// `renderTrigger({ open, toggle })` sostituisce il campo con un attivatore
// proprio (es. una pillola di stato in una tabella), tenendo lo stesso calendario.
export function DatePicker({ value, onChange, className = "", placeholder = "Seleziona data", renderTrigger, ...rest }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(() => {
    const d = value ? new Date(`${value}T00:00:00`) : new Date();
    return { y: d.getFullYear(), m: d.getMonth() };
  });
  const ref = useRef(null);
  const chiudi = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const d = value ? new Date(`${value}T00:00:00`) : new Date();
    setView({ y: d.getFullYear(), m: d.getMonth() });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const oggi = toIsoLocale(new Date());
  const label = value ? value.split("-").reverse().join("/") : placeholder;

  const spostaMese = (delta) => setView(({ y, m }) => {
    const d = new Date(y, m + delta, 1);
    return { y: d.getFullYear(), m: d.getMonth() };
  });

  // Griglia di 6 settimane: i giorni dei mesi adiacenti riempiono i bordi,
  // così l'altezza del popover non salta cambiando mese.
  const primo = new Date(view.y, view.m, 1);
  const offset = (primo.getDay() + 6) % 7;
  const giorni = Array.from({ length: 42 }, (_, i) => new Date(view.y, view.m, 1 - offset + i));

  const pick = (iso) => { onChange(iso); setOpen(false); };

  return (
    <div className="relative" ref={ref}>
      {renderTrigger ? renderTrigger({ open, toggle: () => setOpen(o => !o) }) : (
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className={`${className} w-full flex items-center justify-between gap-2 text-left`}
          {...rest}
        >
          <span className={value ? "scheda-font-mono" : "text-[#9095a1]"}>{label}</span>
          <CalendarDays className="w-4 h-4 text-[#9095a1] shrink-0" strokeWidth={1.8} />
        </button>
      )}
      <PopoverAncorato anchorRef={ref} open={open} onClose={chiudi} width={268}>
          <div className="flex items-center justify-between px-1 py-1 mb-1">
            <button type="button" onClick={() => spostaMese(-1)} aria-label="Mese precedente" className="w-7 h-7 rounded-[8px] flex items-center justify-center text-[#565d6d] hover:bg-[#f3f3f8] transition-colors">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-[13.5px] font-bold text-[#15161c]">
              {MESI_LUNGHI[view.m]} <span className="scheda-font-mono">{view.y}</span>
            </span>
            <button type="button" onClick={() => spostaMese(1)} aria-label="Mese successivo" className="w-7 h-7 rounded-[8px] flex items-center justify-center text-[#565d6d] hover:bg-[#f3f3f8] transition-colors">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-0.5">
            {GIORNI_SETTIMANA.map((g, i) => (
              <span key={i} className="h-7 flex items-center justify-center text-[11px] font-semibold uppercase text-[#9095a1]">{g}</span>
            ))}
            {giorni.map((d) => {
              const iso = toIsoLocale(d);
              const fuoriMese = d.getMonth() !== view.m;
              const selezionato = iso === value;
              const eOggi = iso === oggi;
              return (
                <button
                  type="button"
                  key={iso}
                  onClick={() => pick(iso)}
                  className={`h-8 rounded-[8px] text-[12.5px] scheda-font-mono transition-colors ${
                    selezionato
                      ? "bg-[#5b5bd6] text-white font-semibold"
                      : eOggi
                        ? "text-[#5b5bd6] font-semibold ring-1 ring-inset ring-[#c9c9f0] hover:bg-[#eeeefc]"
                        : fuoriMese
                          ? "text-[#c3c6cf] hover:bg-[#f3f3f8]"
                          : "text-[#3c414d] hover:bg-[#f3f3f8]"
                  }`}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#f0f0f6] px-1">
            <button type="button" onClick={() => pick("")} className="text-[12px] font-semibold text-[#9095a1] hover:text-[#565d6d] transition-colors">
              Cancella
            </button>
            <button type="button" onClick={() => pick(oggi)} className="text-[12px] font-semibold text-[#5b5bd6] hover:text-[#4a4ac4] transition-colors">
              Oggi
            </button>
          </div>
      </PopoverAncorato>
    </div>
  );
}

// --- Scadenza contratto -------------------------------------------------
// Un contratto si rinnova solo nell'ultimo mese prima della scadenza (o dopo).
// Stesso valore di FINESTRA_RINNOVO_MESI in backend/routes/contratti.py, che
// rifiuta comunque un rinnovo aperto prima.
export const FINESTRA_RINNOVO_MESI = 1;

const GIORNO_MS = 1000 * 60 * 60 * 24;

// Data da cui un contratto con questa scadenza si può rinnovare.
export function aperturaRinnovo(scadenza) {
  if (!scadenza) return null;
  const d = new Date(scadenza);
  d.setMonth(d.getMonth() - FINESTRA_RINNOVO_MESI);
  return d;
}

export function rinnovoAperto(scadenza, adesso = new Date()) {
  const apertura = aperturaRinnovo(scadenza);
  return !apertura || adesso >= apertura;
}

// Stato del periodo di un contratto: quanto ne è trascorso, quanti giorni
// mancano e il tono (futuro: non ancora iniziato, es. un rinnovo firmato che
// decorre alla scadenza del precedente; urgente: ultimi 30 giorni).
export function statoScadenza(inizio, scadenza, adesso = new Date()) {
  if (!scadenza) return null;
  const fine = new Date(scadenza);
  const partenza = inizio ? new Date(inizio) : null;
  const giorni = Math.ceil((fine - adesso) / GIORNO_MS);
  if (partenza && adesso < partenza) {
    return { tono: "futuro", trascorso: 0, giorni, giorniAlInizio: Math.ceil((partenza - adesso) / GIORNO_MS), inizio: partenza, fine };
  }
  const totale = partenza ? fine - partenza : 0;
  const trascorso = totale > 0 ? Math.min(1, Math.max(0, (adesso - partenza) / totale)) : giorni < 0 ? 1 : 0;
  const tono = giorni < 0 ? "scaduto" : giorni <= 30 ? "urgente" : "ok";
  return { tono, trascorso, giorni, inizio: partenza, fine };
}

const TONI_SCADENZA = {
  ok: { testo: "#0f7040", barra: "#12a05c" },
  urgente: { testo: "#b45309", barra: "#f59e0b" },
  scaduto: { testo: "#c0392b", barra: "#c0392b" },
  futuro: { testo: "#5b5bd6", barra: "#c9c9f0" },
};

// Testo del tooltip "Periodo del contratto": date, quota trascorsa e da quando
// si può rinnovare. Condiviso fra BarraScadenza e la lista della pagina
// Contratti, che disegna la barra in forma compatta ma deve dire le stesse cose.
export function descrizionePeriodo(inizio, scadenza, rinnovato = false) {
  const s = statoScadenza(inizio, scadenza);
  if (!s) return "";
  const data = (d) => d.toLocaleDateString("it-IT");
  return [
    s.inizio ? `Dal ${data(s.inizio)} al ${data(s.fine)}.` : `Scade il ${data(s.fine)}.`,
    s.tono !== "futuro" && s.inizio ? `Trascorso il ${Math.round(s.trascorso * 100)}% del periodo.` : null,
    rinnovato
      ? "Già rinnovato: il periodo prosegue col contratto di rinnovo."
      : s.tono === "futuro" ? "Non ancora iniziato."
      : rinnovoAperto(scadenza) ? "Rinnovabile ora."
      : `Rinnovabile dal ${data(aperturaRinnovo(scadenza))}, un mese prima della scadenza.`,
  ].filter(Boolean).join(" ");
}

// Barra del tempo trascorso di un contratto, con i giorni alla scadenza e un
// tooltip con date e finestra di rinnovo. Stessa lettura della barra nella
// lista clienti (ClientsPage): la lunghezza del riempimento dice anche la
// durata complessiva, non solo l'urgenza.
export function BarraScadenza({ inizio, scadenza, rinnovato = false, testId }) {
  const s = statoScadenza(inizio, scadenza);
  if (!s) return null;
  const t = TONI_SCADENZA[s.tono];
  const giorniFmt = (n) => Math.abs(n).toLocaleString("it-IT");
  const etichetta = s.tono === "futuro"
    ? `Decorre tra ${giorniFmt(s.giorniAlInizio)} gg`
    : s.tono === "scaduto" ? `Scaduto da ${giorniFmt(s.giorni)} gg`
    : s.giorni === 0 ? "Scade oggi"
    : `${giorniFmt(s.giorni)} gg alla scadenza`;
  return (
    <InfoTooltip title="Periodo del contratto" description={descrizionePeriodo(inizio, scadenza, rinnovato)} block>
      <div className="w-full mt-2 scheda-font-ui" data-testid={testId}>
        <div className="h-1.5 rounded-full bg-[#e9e9f1] overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `${Math.round(s.trascorso * 100)}%`, background: t.barra }} />
        </div>
        <span className="text-[11.5px] font-semibold mt-1 inline-block" style={{ color: rinnovato ? "#9095a1" : t.testo }}>
          {rinnovato ? `${etichetta} · rinnovato` : etichetta}
        </span>
      </div>
    </InfoTooltip>
  );
}
